import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Button,
  Typography,
  Spin,
  message,
  Timeline,
  Avatar,
  DatePicker,
  Divider,
} from "antd";
import {
  UserOutlined,
  ShoppingCartOutlined,
  DollarCircleOutlined,
  RiseOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ShopOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FireOutlined,
  StarOutlined,
  RocketOutlined,
  ScheduleOutlined,
  TagOutlined,
  DashboardOutlined,
  BarChartOutlined,
  EyeOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  SafetyOutlined,
  LineChartOutlined,
  BellOutlined,
  SolutionOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ChartTooltip,
  ChartLegend,
  ChartDataLabels
);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const EMPTY_DATA = {
  highIntentLeads: [],
  directRegisteredUsers: [],
  pendingUsers: [],
  pendingReactivations: [],
  usersList: [],
  ordersSummary: { TotalOrders: 0, TotalQuantity: 0, TotalRevenue: 0 },
  prevOrdersSummary: { TotalOrders: 0, TotalQuantity: 0, TotalRevenue: 0 },
  recentOrders: [],
  downloadsSummary: { TotalDownloads: 0 },
  popularDownloads: [],
  productStatus: { Total: 0, Active: 0, Inactive: 0, Published: 0, NotPublished: 0 },
  leadsSummary: { TodayIndiaMart: 0, TodayTradeIndia: 0, TodayTotal: 0, MonthlyTrend: [] },
  visitorJourney: { TotalEvents: 0, UniqueVisitors: 0, ByScreen: [], TodaysUniqueVisitors: 0, TodaysVisitors: [] },
  topViewedProducts: [],
  topBrowsedCategories: [],
  outOfStockDemand: [],
  recentActivities: [],
};

const getAuthToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const safeGet = async (token, path, params) => {
  if (!token || !global.config?.APIURL?.BaseURL) return null;
  try {
    const res = await axios.get(`${global.config.APIURL.BaseURL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data;
  } catch (err) {
    console.error(`Error fetching ${path}:`, err);
    return null;
  }
};

const AdminDashBoard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().subtract(29, "day"), dayjs()]);
  const [data, setData] = useState(EMPTY_DATA);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const from = dateRange[0].format("DD-MM-YYYY");
      const to = dateRange[1].format("DD-MM-YYYY");
      const diffDays = dateRange[1].diff(dateRange[0], "day") + 1;
      const prevTo = dateRange[0].subtract(1, "day");
      const prevFrom = prevTo.subtract(diffDays - 1, "day");
      const prevFromStr = prevFrom.format("DD-MM-YYYY");
      const prevToStr = prevTo.format("DD-MM-YYYY");

      const [
        highIntentLeads,
        directRegisteredUsers,
        pendingUsers,
        pendingReactivations,
        usersList,
        ordersSummary,
        prevOrdersSummary,
        recentOrders,
        downloadsSummary,
        popularDownloads,
        productStatus,
        leadsSummary,
        visitorJourney,
        topViewedProducts,
        topBrowsedCategories,
        outOfStockDemand,
        recentActivities,
      ] = await Promise.all([
        safeGet(token, "api/HighIntentLeads/GetHighIntentLeads"),
        safeGet(token, "api/HighIntentLeads/GetDirectRegisteredUsers"),
        safeGet(token, "api/User/GetListOfPendingUsers"),
        safeGet(token, "api/ReactivationAdmin/GetPendingRequests"),
        safeGet(token, "api/User/GetListOfUsersbyEmployee"),
        safeGet(token, "api/Order/GetOrdersSummaryForAdmin", { From: from, To: to }),
        safeGet(token, "api/Order/GetOrdersSummaryForAdmin", { From: prevFromStr, To: prevToStr }),
        safeGet(token, "api/Order/GetRecentOrdersForAdmin", { Top: 10 }),
        safeGet(token, "api/MyDownload/GetDownloadsSummaryForAdmin", { From: from, To: to }),
        safeGet(token, "api/MyDownload/GetPopularDownloadsForAdmin", { Top: 10, From: from, To: to }),
        safeGet(token, "api/ProductAdminEdit/GetProductStatusSummary"),
        safeGet(token, "api/Lead/GetLeadsSummary", { Months: 6 }),
        safeGet(token, "api/ActivityAnalytics/GetVisitorJourneySummary", { From: from, To: to, Top: 10 }),
        safeGet(token, "api/ActivityAnalytics/GetTopViewedProducts", { From: from, To: to, Top: 10 }),
        safeGet(token, "api/ActivityAnalytics/GetTopBrowsedCategories", { From: from, To: to, Top: 10 }),
        safeGet(token, "api/ActivityAnalytics/GetOutOfStockDemand", { From: from, To: to, Top: 10 }),
        safeGet(token, "api/UserActivity/GetRecentActivities"),
      ]);

      setData({
        highIntentLeads: Array.isArray(highIntentLeads) ? highIntentLeads : [],
        directRegisteredUsers: Array.isArray(directRegisteredUsers) ? directRegisteredUsers : [],
        pendingUsers: Array.isArray(pendingUsers) ? pendingUsers : [],
        pendingReactivations: Array.isArray(pendingReactivations) ? pendingReactivations : [],
        usersList: Array.isArray(usersList) ? usersList : [],
        ordersSummary: ordersSummary || EMPTY_DATA.ordersSummary,
        prevOrdersSummary: prevOrdersSummary || EMPTY_DATA.prevOrdersSummary,
        recentOrders: Array.isArray(recentOrders) ? recentOrders : [],
        downloadsSummary: downloadsSummary || EMPTY_DATA.downloadsSummary,
        popularDownloads: Array.isArray(popularDownloads) ? popularDownloads : [],
        productStatus: productStatus || EMPTY_DATA.productStatus,
        leadsSummary: leadsSummary || EMPTY_DATA.leadsSummary,
        visitorJourney: visitorJourney || EMPTY_DATA.visitorJourney,
        topViewedProducts: Array.isArray(topViewedProducts) ? topViewedProducts : [],
        topBrowsedCategories: Array.isArray(topBrowsedCategories) ? topBrowsedCategories : [],
        outOfStockDemand: Array.isArray(outOfStockDemand) ? outOfStockDemand : [],
        recentActivities: Array.isArray(recentActivities) ? recentActivities : [],
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      message.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    message.success("Dashboard refreshed successfully");
  };

  const quickActionsData = [
    { title: "Dashboard", icon: <DashboardOutlined />, action: () => navigate("/AdminDashBoard"), color: "#1890ff" },
    { title: "Manage Product", icon: <ShopOutlined />, action: () => navigate("/AdminCategory"), color: "#52c41a" },
    { title: "Product Tags", icon: <TagOutlined />, action: () => navigate("/ManageProductTags"), color: "#fa8c16" },
    { title: "LastChance Deals", icon: <FireOutlined />, action: () => navigate("/ManageLastChanceDeals"), color: "#ff4d4f" },
    { title: "Trending Items", icon: <RiseOutlined />, action: () => navigate("/ManageTrendingItems"), color: "#13c2c2" },
    { title: "User List", icon: <UserOutlined />, action: () => navigate("/UserList"), color: "#eb2f96" },
    { title: "Activity Summary", icon: <BarChartOutlined />, action: () => navigate("/UserActivitySummary"), color: "#f5222d" },
    { title: "Insta Post Schedule", icon: <ScheduleOutlined />, action: () => navigate("/InstaPostSchedule"), color: "#a0d911" },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text>Loading dashboard...</Text>
        </div>
      </div>
    );
  }

  // Derived: Users Overview
  const totalUsers = data.usersList.length;
  const activeUsers = data.usersList.filter((u) => u.IsActive).length;
  const pendingApprovalUsers = data.usersList.filter((u) => !u.IsApproved).length;
  const activeRate = totalUsers ? Math.round((activeUsers / totalUsers) * 100) : 0;

  // Derived: Orders growth (real, period-over-period)
  const revenueGrowth = data.prevOrdersSummary.TotalRevenue
    ? ((data.ordersSummary.TotalRevenue - data.prevOrdersSummary.TotalRevenue) /
        data.prevOrdersSummary.TotalRevenue) *
      100
    : data.ordersSummary.TotalRevenue > 0
    ? 100
    : 0;
  const growthColor = revenueGrowth >= 0 ? "#52c41a" : "#ff4d4f";
  const GrowthIcon = revenueGrowth >= 0 ? ArrowUpOutlined : ArrowDownOutlined;

  // Derived: Leads chart data
  const leadsChartData = {
    labels: data.leadsSummary.MonthlyTrend.map((m) => m.MonthLabel),
    datasets: [
      {
        label: "IndiaMart",
        data: data.leadsSummary.MonthlyTrend.map((m) => m.IndiaMartCount),
        backgroundColor: "#1890ff",
      },
      {
        label: "TradeIndia",
        data: data.leadsSummary.MonthlyTrend.map((m) => m.TradeIndiaCount),
        backgroundColor: "#fa8c16",
      },
    ],
  };
  const leadsChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: "top" },
      tooltip: { enabled: true },
      datalabels: {
        display: true,
        color: "#222",
        anchor: "end",
        align: "top",
        font: { size: 10 },
      },
    },
    scales: {
      x: { title: { display: true, text: "Month" } },
      y: { title: { display: true, text: "Leads" }, beginAtZero: true },
    },
  };

  const recentOrdersColumns = [
    { title: "Order ID", dataIndex: "OrderID", key: "OrderID", render: (id) => <Text strong>#{id}</Text> },
    { title: "Customer", dataIndex: "CustomerName", key: "CustomerName", render: (v, r) => v || r.FirmName || "-" },
    { title: "Date", dataIndex: "OrderDate", key: "OrderDate", render: (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-") },
    { title: "Qty", dataIndex: "TotalQuantity", key: "TotalQuantity" },
    { title: "Amount", dataIndex: "GrandTotal", key: "GrandTotal", render: (v) => `₹${(v || 0).toLocaleString("en-IN")}` },
  ];

  const topViewedColumns = [
    { title: "Product", dataIndex: "ProductName", key: "ProductName" },
    { title: "Design No", dataIndex: "DesignNo", key: "DesignNo" },
    { title: "Views", dataIndex: "ViewCount", key: "ViewCount", sorter: (a, b) => a.ViewCount - b.ViewCount, defaultSortOrder: "descend" },
    {
      title: "Stock",
      dataIndex: "IsInStock",
      key: "IsInStock",
      render: (v) => <Tag color={v ? "green" : "red"}>{v ? "IN STOCK" : "OUT OF STOCK"}</Tag>,
    },
  ];

  const topCategoriesColumns = [
    { title: "Category", dataIndex: "CategoryName", key: "CategoryName" },
    { title: "Views", dataIndex: "ViewCount", key: "ViewCount", sorter: (a, b) => a.ViewCount - b.ViewCount, defaultSortOrder: "descend" },
    { title: "Unique Users", dataIndex: "UniqueUsers", key: "UniqueUsers" },
  ];

  const outOfStockColumns = [
    { title: "Product", dataIndex: "ProductName", key: "ProductName" },
    { title: "Design No", dataIndex: "DesignNo", key: "DesignNo" },
    { title: "Views (Out of Stock)", dataIndex: "ViewsWhileOutOfStock", key: "ViewsWhileOutOfStock", sorter: (a, b) => a.ViewsWhileOutOfStock - b.ViewsWhileOutOfStock, defaultSortOrder: "descend" },
    { title: "Unique Viewers", dataIndex: "UniqueViewers", key: "UniqueViewers" },
    {
      title: "Now In Stock",
      dataIndex: "IsNowInStock",
      key: "IsNowInStock",
      render: (v) => <Tag color={v ? "green" : "orange"}>{v ? "YES" : "NO"}</Tag>,
    },
  ];

  const popularDownloadsColumns = [
    { title: "Product", dataIndex: "ProductName", key: "ProductName" },
    { title: "Design No", dataIndex: "DesignNo", key: "DesignNo" },
    { title: "Downloads", dataIndex: "DownloadCount", key: "DownloadCount", sorter: (a, b) => a.DownloadCount - b.DownloadCount, defaultSortOrder: "descend" },
  ];

  const pendingUsersColumns = [
    { title: "Firm", dataIndex: "FirmName", key: "FirmName" },
    { title: "Contact", dataIndex: "ContactName", key: "ContactName" },
    { title: "Mobile", dataIndex: "ContactNumber", key: "ContactNumber" },
    { title: "City", dataIndex: "City", key: "City" },
    { title: "Registered On", dataIndex: "RegistrationTime", key: "RegistrationTime", render: (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-") },
  ];

  const pendingReactivationColumns = [
    { title: "Firm", dataIndex: "FirmName", key: "FirmName" },
    { title: "Contact", dataIndex: "ContactPersonName", key: "ContactPersonName" },
    { title: "Mobile", dataIndex: "MobileNo", key: "MobileNo" },
    { title: "Requested On", dataIndex: "RequestedOn", key: "RequestedOn", render: (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-") },
  ];

  const visitorScreenColumns = [
    { title: "Screen", dataIndex: "ScreenName", key: "ScreenName", render: (v) => v || "Unknown" },
    { title: "Views", dataIndex: "ViewCount", key: "ViewCount" },
    { title: "Unique Users", dataIndex: "UniqueUsers", key: "UniqueUsers" },
  ];

  const todaysVisitorColumns = [
    { title: "Visitor", dataIndex: "Name", key: "Name", render: (v, row) => v || row.MobileNo || "Unknown" },
    { title: "City", dataIndex: "City", key: "City" },
    { title: "Pages Explored", dataIndex: "PagesExplored", key: "PagesExplored" },
  ];

  return (
    <div style={{ padding: "24px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <Row gutter={[0, 12]} justify="space-between" align="middle" wrap>
          <Col xs={24} sm={12}>
            <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
              <DashboardOutlined style={{ marginRight: "8px" }} />
              Admin Dashboard
            </Title>
            <Text type="secondary">Welcome back! Here's what's happening.</Text>
          </Col>
          <Col xs={24} sm={12} style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            <RangePicker
              value={dateRange}
              onChange={(v) => v && v[0] && v[1] && setDateRange(v)}
              format="DD-MM-YYYY"
              allowClear={false}
              size="small"
            />
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={refreshing} type="primary" size="small">
              Refresh
            </Button>
          </Col>
        </Row>
      </div>

      {/* Business KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={data.ordersSummary.TotalOrders}
              prefix={<ShoppingCartOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={data.ordersSummary.TotalRevenue}
              prefix={<DollarCircleOutlined style={{ color: "#fa8c16" }} />}
              formatter={(value) => `₹${Number(value).toLocaleString("en-IN")}`}
              valueStyle={{ color: "#fa8c16" }}
            />
            <Text style={{ fontSize: "12px", color: growthColor }}>
              <GrowthIcon style={{ marginRight: "4px" }} />
              {Math.abs(revenueGrowth).toFixed(1)}% vs previous period
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Downloads"
              value={data.downloadsSummary.TotalDownloads}
              prefix={<DownloadOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/AdminAllProducts")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Total Products"
              value={data.productStatus.Total}
              prefix={<AppstoreOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
            />
            <Row gutter={8} style={{ marginTop: "8px" }}>
              <Col span={8}><Text type="secondary" style={{ fontSize: 11 }}>Active: {data.productStatus.Active}</Text></Col>
              <Col span={8}><Text type="secondary" style={{ fontSize: 11 }}>Inactive: {data.productStatus.Inactive}</Text></Col>
              <Col span={8}><Text type="secondary" style={{ fontSize: 11 }}>Unpublished: {data.productStatus.NotPublished}</Text></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* CRM KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/LeadsToApprove")} style={{ cursor: "pointer" }}>
            <Statistic
              title="High Intent Leads"
              value={data.highIntentLeads.length}
              prefix={<FireOutlined style={{ color: "#ff4d4f" }} />}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/DirectRegistrationRequests")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Direct Registered Users"
              value={data.directRegisteredUsers.length}
              prefix={<SolutionOutlined style={{ color: "#13c2c2" }} />}
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/UserList")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Pending User Approvals"
              value={data.pendingUsers.length}
              prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable onClick={() => navigate("/ReactivationRequests")} style={{ cursor: "pointer" }}>
            <Statistic
              title="Pending Reactivation Requests"
              value={data.pendingReactivations.length}
              prefix={<SafetyOutlined style={{ color: "#eb2f96" }} />}
              valueStyle={{ color: "#eb2f96" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card title={<><RocketOutlined /> Quick Actions</>} style={{ marginBottom: "16px" }}>
        <Row gutter={[16, 16]}>
          {quickActionsData.map((item, index) => (
            <Col xs={24} sm={12} md={6} lg={4} xl={3} key={index}>
              <Card hoverable size="small" style={{ textAlign: "center", borderColor: item.color }} onClick={item.action}>
                <div style={{ color: item.color, fontSize: "24px", marginBottom: "8px" }}>{item.icon}</div>
                <Text strong style={{ fontSize: "12px" }}>{item.title}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Leads + Visitor Journey */}
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} lg={12}>
          <Card title={<><GlobalOutlined /> Leads (IndiaMart / TradeIndia)</>}>
            <Row gutter={16} style={{ marginBottom: "16px" }}>
              <Col span={8}>
                <Statistic title="Today - IndiaMart" value={data.leadsSummary.TodayIndiaMart} valueStyle={{ fontSize: 18, color: "#1890ff" }} />
              </Col>
              <Col span={8}>
                <Statistic title="Today - TradeIndia" value={data.leadsSummary.TodayTradeIndia} valueStyle={{ fontSize: 18, color: "#fa8c16" }} />
              </Col>
              <Col span={8}>
                <Statistic title="Today - Total" value={data.leadsSummary.TodayTotal} valueStyle={{ fontSize: 18 }} />
              </Col>
            </Row>
            {data.leadsSummary.MonthlyTrend.length > 0 ? (
              <Bar data={leadsChartData} options={leadsChartOptions} height={90} />
            ) : (
              <Text type="secondary">No lead trend data available.</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><EyeOutlined /> Visitor Journey</>}>
            <Row gutter={16} style={{ marginBottom: "16px" }}>
              <Col span={12}>
                <Statistic title="Total Events" value={data.visitorJourney.TotalEvents} valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col span={12}>
                <Statistic title="Unique Visitors" value={data.visitorJourney.UniqueVisitors} valueStyle={{ fontSize: 18, color: "#52c41a" }} />
              </Col>
            </Row>
            <Table
              columns={visitorScreenColumns}
              dataSource={data.visitorJourney.ByScreen}
              pagination={false}
              size="small"
              rowKey="ScreenID"
            />

            <Divider style={{ margin: "16px 0" }} />

            <Statistic
              title="Today's Unique Visitors"
              value={data.visitorJourney.TodaysUniqueVisitors}
              valueStyle={{ fontSize: 18, color: "#1890ff" }}
              style={{ marginBottom: "12px" }}
            />
            <Table
              columns={todaysVisitorColumns}
              dataSource={data.visitorJourney.TodaysVisitors}
              pagination={false}
              size="small"
              rowKey="UserID"
              locale={{ emptyText: "No visitors yet today" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Users Overview + Orders Trend */}
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} lg={12}>
          <Card title={<><TeamOutlined /> Users Overview</>} extra={<Button type="link" onClick={() => navigate("/UserList")}>View All</Button>}>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="Active Users" value={activeUsers} prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />} valueStyle={{ color: "#52c41a", fontSize: "18px" }} />
              </Col>
              <Col span={12}>
                <Statistic title="Pending Approval" value={pendingApprovalUsers} prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />} valueStyle={{ color: "#faad14", fontSize: "18px" }} />
              </Col>
            </Row>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Progress type="circle" percent={activeRate} size={80} strokeColor="#52c41a" />
              <div style={{ marginTop: "8px" }}>
                <Text type="secondary">Active Rate ({totalUsers} total)</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><LineChartOutlined /> Orders Trend</>}>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="Orders (selected range)" value={data.ordersSummary.TotalOrders} valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col span={12}>
                <Statistic title="Quantity (selected range)" value={data.ordersSummary.TotalQuantity} valueStyle={{ fontSize: 18 }} />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Revenue change vs. the equivalent prior period</Text>
              <div style={{ fontSize: 20, color: growthColor, fontWeight: "bold" }}>
                <GrowthIcon /> {Math.abs(revenueGrowth).toFixed(1)}%
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Orders + Recent Activity */}
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} lg={12}>
          <Card title={<><ShoppingCartOutlined /> Recent Orders</>}>
            <Table columns={recentOrdersColumns} dataSource={data.recentOrders} pagination={false} size="small" rowKey="OrderID" />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><BellOutlined /> Recent Activity</>} extra={<Button type="link" onClick={() => navigate("/UserActivitySummary")}>View All</Button>}>
            <Timeline size="small">
              {data.recentActivities.map((activity, index) => (
                <Timeline.Item key={index} dot={<Avatar size="small" icon={<UserOutlined />} />}>
                  <div>
                    <Text strong>{activity.ScreenName}</Text>{activity.DetailName ? ` — ${activity.DetailName}` : ""}
                    <br />
                    <Text type="secondary">{activity.Name} • {activity.DateTime ? dayjs(activity.DateTime).format("DD-MM-YYYY HH:mm") : ""}</Text>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* Top Viewed Products + Top Browsed Categories */}
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} lg={12}>
          <Card title={<><StarOutlined /> Top Viewed Products</>} extra={<Button type="link" onClick={() => navigate("/ProductInsights")}>View All</Button>}>
            <Table columns={topViewedColumns} dataSource={data.topViewedProducts} pagination={false} size="small" rowKey="ProductID" />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><BarChartOutlined /> Top Browsed Categories</>} extra={<Button type="link" onClick={() => navigate("/ProductInsights")}>View All</Button>}>
            <Table columns={topCategoriesColumns} dataSource={data.topBrowsedCategories} pagination={false} size="small" rowKey="CategoryID" />
          </Card>
        </Col>
      </Row>

      {/* Out of Stock Demand + Popular Downloads */}
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col xs={24} lg={12}>
          <Card title={<><WarningOutlined /> Out-of-Stock Demand</>} extra={<Button type="link" onClick={() => navigate("/ProductInsights")}>View All</Button>}>
            <Table columns={outOfStockColumns} dataSource={data.outOfStockDemand} pagination={false} size="small" rowKey="ProductID" />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><DownloadOutlined /> Popular Downloads</>}>
            <Table columns={popularDownloadsColumns} dataSource={data.popularDownloads} pagination={false} size="small" rowKey="ProductID" />
          </Card>
        </Col>
      </Row>

      {/* Pending Approvals + Pending Reactivations */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<><ClockCircleOutlined /> Pending User Approvals</>} extra={<Button type="link" onClick={() => navigate("/UserList")}>View All</Button>}>
            <Table columns={pendingUsersColumns} dataSource={data.pendingUsers} pagination={false} size="small" rowKey="UserID" />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><SafetyOutlined /> Pending Reactivation Requests</>} extra={<Button type="link" onClick={() => navigate("/ReactivationRequests")}>View All</Button>}>
            <Table columns={pendingReactivationColumns} dataSource={data.pendingReactivations} pagination={false} size="small" rowKey="RequestID" />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashBoard;
